import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
    initiatePayment,
    openEpaycoCheckout,
    getPaymentConfig,
    getPaymentHistory,
} from './paymentsApi';
import type { PaymentTransaction } from './types';

export function usePayment() {
    const [loading, setLoading] = useState(false);
    const [enabled, setEnabled] = useState(true);
    const [configLoaded, setConfigLoaded] = useState(false);
    const [pricePerMessage, setPricePerMessage] = useState(95);
    const [internationalEnabled, setInternationalEnabled] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const cfg = await getPaymentConfig();
                setEnabled(cfg.enabled !== false);
                setPricePerMessage(cfg.price_per_message ?? 95);
                setInternationalEnabled(!!cfg.international_enabled);
            } catch {
                setEnabled(true);
            } finally {
                setConfigLoaded(true);
            }
        };
        load();
    }, []);

    const calculateMessages = useCallback(
        (amount: number) => {
            if (!pricePerMessage || amount <= 0) return 0;
            return Math.floor(amount / pricePerMessage);
        },
        [pricePerMessage]
    );

    const pay = useCallback(async (amount: number, planType: string) => {
        setLoading(true);
        try {
            const formParams = await initiatePayment({ amount, plan_type: planType });
            toast.success('Abriendo checkout seguro...');
            await openEpaycoCheckout(formParams);
        } catch (err: any) {
            const detail = err?.response?.data?.detail || err?.message || 'No se pudo iniciar el pago';
            toast.error(detail);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        pay,
        loading,
        enabled,
        configLoaded,
        pricePerMessage,
        calculateMessages,
        internationalEnabled,
    };
}

export function usePaymentHistory() {
    const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getPaymentHistory();
            setTransactions(res.transactions || []);
        } catch {
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { transactions, loading, refresh };
}
